// Mughal-inspired ornament system — cusped arches, jali latticework, paisley
// vines, lotus ponds, rosette chains, corner flourishes. Good Earth / Mughal
// art vocabulary, drawn as fine line-work. Colour via `currentColor`.

// A row of cusped (scalloped) Mughal arches — the frieze that crowns a section.
export const ArchFrieze = ({ className = "", style = {} }) => {
  const arch =
    "M0,38 L0,24 C0,14 6,12 10,11 C10,5 13,1 18,1 C23,1 26,5 26,11 C30,12 36,14 36,24 L36,38";
  const cols = 32;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${cols * 36} 40`}
      preserveAspectRatio="xMidYMax slice"
      className={`block h-8 w-full md:h-10 ${className}`}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
    >
      <line x1="0" y1="38.5" x2={cols * 36} y2="38.5" />
      {Array.from({ length: cols }, (_, i) => (
        <g key={i} transform={`translate(${i * 36},0)`}>
          <path d={arch} />
          <circle cx="18" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </g>
      ))}
    </svg>
  );
};

// A jali lattice rule — diamonds and seeds.
export const JaliRule = ({ className = "", style = {} }) => {
  const cell = (x) => (
    <g key={x} transform={`translate(${x},0)`}>
      <rect x="9" y="3" width="6" height="6" transform="rotate(45 12 6)" />
      <circle cx="0" cy="6" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="24" cy="6" r="0.9" fill="currentColor" stroke="none" />
    </g>
  );
  const cols = 42;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${cols * 24} 12`}
      preserveAspectRatio="xMidYMid slice"
      className={`block h-3 w-full ${className}`}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.8"
    >
      {Array.from({ length: cols }, (_, i) => cell(i * 24))}
    </svg>
  );
};

// Star-jali — the eight-pointed star lattice of Mughal screens.
export const StarJaliRule = ({ className = "", style = {} }) => {
  const cell = (x) => (
    <g key={x} transform={`translate(${x},0)`}>
      <rect x="7" y="1" width="14" height="14" />
      <rect x="7" y="1" width="14" height="14" transform="rotate(45 14 8)" />
      <circle cx="14" cy="8" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="0" cy="8" r="0.8" fill="currentColor" stroke="none" />
      <circle cx="28" cy="8" r="0.8" fill="currentColor" stroke="none" />
    </g>
  );
  const cols = 36;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${cols * 28} 16`}
      preserveAspectRatio="xMidYMid slice"
      className={`block h-4 w-full ${className}`}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.7"
    >
      {Array.from({ length: cols }, (_, i) => cell(i * 28))}
    </svg>
  );
};

// Paisley vine — running boteh with a curling tip and seed flower between.
export const PaisleyRule = ({ className = "", style = {} }) => {
  const cell = (x) => (
    <g key={x} transform={`translate(${x},0)`}>
      <path d="M8,18 C3,15 2,9 6,5 C10,1 17,2 19,7 C21,11 18,15 14,14.5 C11.5,14.2 10.5,11.5 12,9.8 C13.2,8.4 15.4,9 15.4,10.8" />
      <path d="M8,18 C14,20 22,19 30,17" />
      <path d="M34,10 c0,-2.4 2,-3.4 3.2,-2 c1.2,-1.4 3.2,-0.4 3.2,2 c0,2.2 -1.6,3.4 -3.2,4.4 c-1.6,-1 -3.2,-2.2 -3.2,-4.4 z" />
    </g>
  );
  const cols = 24;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${cols * 44} 22`}
      preserveAspectRatio="xMidYMid slice"
      className={`block h-5 w-full ${className}`}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.9"
    >
      {Array.from({ length: cols }, (_, i) => cell(i * 44))}
    </svg>
  );
};

// Lotus pond frieze — bloom and bud alternating on a water line.
export const LotusPondRule = ({ className = "", style = {} }) => {
  const bloom = (x) => (
    <g key={`b${x}`} transform={`translate(${x},0)`}>
      <path d="M0,24 C-1,17 1,12 5,9 C6,5 9,3 12,3 C15,3 18,5 19,9 C23,12 25,17 24,24" />
      <path d="M5,24 C4,18 6,14 9,12 M19,24 C20,18 18,14 15,12 M12,24 L12,8" />
      <path d="M-6,24 Q0,21 6,24 M18,24 Q24,21 30,24" />
    </g>
  );
  const bud = (x) => (
    <g key={`u${x}`} transform={`translate(${x},0)`}>
      <path d="M0,24 C-1,18 0,13 3,10 C4,7 6,5 8,5 C10,5 12,7 13,10 C16,13 17,18 16,24" />
      <path d="M8,24 L8,10" />
    </g>
  );
  const cols = 18;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${cols * 60} 27`}
      preserveAspectRatio="xMidYMid slice"
      className={`block h-6 w-full ${className}`}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.9"
    >
      {Array.from({ length: cols }, (_, i) => (
        <g key={i} transform={`translate(${i * 60},0)`}>
          {bloom(0)}
          {bud(36)}
        </g>
      ))}
      <line x1="0" y1="25.5" x2={cols * 60} y2="25.5" strokeDasharray="1 4" strokeLinecap="round" />
    </svg>
  );
};

// Rosette chain — pietra dura inlay flowers on a thread.
export const RosetteChain = ({ className = "", style = {} }) => {
  const rosette = (x) => (
    <g key={x} transform={`translate(${x},8)`}>
      {Array.from({ length: 8 }, (_, p) => (
        <ellipse key={p} cx="0" cy="-4.2" rx="1.6" ry="3.4" transform={`rotate(${p * 45})`} />
      ))}
      <circle r="1.1" fill="currentColor" stroke="none" />
    </g>
  );
  const cols = 40;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${cols * 26} 16`}
      preserveAspectRatio="xMidYMid slice"
      className={`block h-4 w-full ${className}`}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth="0.7"
    >
      <line x1="0" y1="8" x2={cols * 26} y2="8" strokeDasharray="0.5 5" strokeLinecap="round" />
      {Array.from({ length: cols }, (_, i) => rosette(i * 26 + 13))}
    </svg>
  );
};

// Section heading inside a small cusped-arch cartouche.
export const ArchCartouche = ({ children, className = "" }) => (
  <span className={`relative inline-flex flex-col items-center gap-2 ${className}`}>
    <svg
      aria-hidden="true"
      viewBox="0 0 36 24"
      className="h-5 w-8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
    >
      <path d="M2,23 L2,14 C2,8 6,7 9,6 C9,2 12,0.5 18,0.5 C24,0.5 27,2 27,6 C30,7 34,8 34,14 L34,23" />
      <circle cx="18" cy="4.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
    {children}
  </span>
);

// Corner flourish — a paisley leaf for frame corners.
export const CornerFlourish = ({ className = "", style = {} }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 60 60"
    className={`h-10 w-10 md:h-14 md:w-14 ${className}`}
    style={style}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
  >
    <path d="M2,58 L2,22 C2,10 10,2 22,2 L58,2" />
    <path d="M2,58 L2,34 C2,20 12,10 26,10 C36,10 42,16 42,24 C42,31 37,35 31,35 C26,35 23,31 24,27 C25,24 28,22 31,24" />
    <circle cx="31" cy="28" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="10" cy="48" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="48" cy="10" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

// Ornamental framed panel — double hairline with flourished corners.
export const OrnamentalFrame = ({ children, className = "" }) => (
  <div
    className={`relative border border-[#d4af37]/30 ${className}`}
    style={{ boxShadow: "inset 0 0 0 5px rgba(29,18,6,1), inset 0 0 0 6px rgba(212,175,55,0.18)" }}
  >
    <CornerFlourish className="absolute -left-1 -top-1 text-[#d4af37]/70" />
    <CornerFlourish className="absolute -right-1 -top-1 rotate-90 text-[#d4af37]/70" />
    <CornerFlourish className="absolute -bottom-1 -right-1 rotate-180 text-[#d4af37]/70" />
    <CornerFlourish className="absolute -bottom-1 -left-1 -rotate-90 text-[#d4af37]/70" />
    {children}
  </div>
);
